import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSObjects = () => {
    const objectFundamentalsCode = `// The standard way to model data
const user = {
    id: 101,
    username: 'js_pro',
    roles: ['admin', 'editor'],
    // Method Shorthand
    logStatus() {
        console.log(\`User \${this.username} is active.\`);
    }
};

// Accessing properties
console.log(user.username); // Dot notation (preferred)
console.log(user['id']);    // Bracket notation (dynamic)`;

    const referenceVsValueCode = `// Objects are held by reference, not value
const original = { score: 10 };
const copy = original; // Both point to the same memory address

copy.score = 99;
console.log(original.score); // 99 (Modified!)

// True Copy (Shallow)
const clone = { ...original };
clone.score = 50;
console.log(original.score); // 99 (Unchanged)`;

    const descriptorsCode = `// Fine-grained control with Property Descriptors
const config = {};

Object.defineProperty(config, 'API_KEY', {
    value: 'secret_123',
    writable: false,     // Cannot reassign
    enumerable: true,    // Shows up in loops
    configurable: false  // Cannot delete or redefine
});

config.API_KEY = 'hacked'; // Fails silently in non-strict mode
console.log(config.API_KEY); // 'secret_123'`;

    const performancePatternsCode = `//  Performance Anti-pattern: Dynamic property deletion
const obj = { x: 1, y: 2 };
delete obj.x; 

//  Why? Engines like V8 use "Hidden Classes". 
// 'delete' changes the object's shape, forcing the engine 
// to drop to a slower "Dictionary Mode" for property lookups.

// Better Alternative: Set to null/undefined if you must clear it
obj.x = undefined;`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Memory & Engine Internals</div>
                <h1>Object Architecture</h1>
                <p className="hero-lead">
                    In JavaScript, objects are much more than simple key-value pairs. They
                    are the core building blocks of the language, optimized by engines
                    via <strong>Hidden Classes (Shapes)</strong> and held in memory via
                    <strong>References</strong>.
                </p>
            </div>

            <div className="content-section">
                <h2>The Foundation of State</h2>
                <p>
                    JavaScript is essentially an "Object-based" language. Whether it's
                    a <code>Function</code>, an <code>Array</code>, or a standard
                    dictionary, they all inherit from <code>Object.prototype</code>.
                    Using <strong>Object Literals</strong> is the most performant
                    and readable way to define state.
                </p>
                <CodeBlock language="javascript" code={objectFundamentalsCode} />
            </div>

            <div className="content-section">
                <h2>References and The Heap</h2>
                <p>
                    Unlike primitives (string, number, boolean) which are copied by
                    <strong>Value</strong>, objects are stored in the memory
                    <strong>Heap</strong> and accessed via a pointer (the <strong>Reference</strong>).
                </p>
                <CodeBlock language="javascript" code={referenceVsValueCode} />
                <ProTip><strong> Senior Note:</strong> Passing an object to a function
                    allows that function to mutate the original object. In modern
                    frameworks like React, this is considered a "Side Effect" and
                    is generally avoided in favor of <strong>Immutability</strong>.</ProTip>
            </div>

            <div className="content-section">
                <h2>Property Descriptors & Security</h2>
                <p>
                    For production-grade libraries, standard object literals often aren't
                    restrictive enough. <code>Object.defineProperty</code> allows you
                    to create "Internal" properties or read-only constants that
                    cannot be compromised by downstream code.
                </p>
                <CodeBlock language="javascript" code={descriptorsCode} />
            </div>

            <div className="content-section">
                <h2>The Performance Tax of 'delete'</h2>
                <p>
                    JavaScript engines (V8, JavaScriptCore) optimize objects by
                    predicting their "Shape". When you use the <code>delete</code>
                    operator, you break this optimization.
                </p>
                <CodeBlock language="javascript" code={performancePatternsCode} />
                <div className="key-point">
                    Building objects with consistent shapes (adding properties in
                    the same order) allows JIT compilers to use <strong>Inline Caching</strong>
                    for near-instant property access.
                </div>
            </div>

            <KeyFacts><ul>
                    <li><strong>Initialization:</strong> Always define all properties at creation time to establish a stable Shape.</li>
                    <li><strong>Immutability:</strong> Use <code>Object.freeze()</code> for configuration objects that should never change.</li>
                    <li><strong>Accessors:</strong> Use <code>get</code> and <code>set</code> keywords for properties requiring validation.</li>
                    <li> <strong>Shape Chaos:</strong> Avoid adding or deleting properties dynamically in performance-critical loops.</li>
                    <li><strong>Composition:</strong> Favor the spread operator <code>{"{ ...obj }"}</code> for producing fresh state updates.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSObjects;


